'use client';

import { useState, useEffect } from 'react';


interface CalculatorInputs {
  currentRevenue: number;
  targetMarketShare: number;
  investmentAmount: number;
  timeframe: number;
}

interface ROIResults {
  projectedRevenue: number;
  roi: number;
  breakEvenMonths: number;
  yearlyGrowth: number;
}

export default function ROICalculator() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [inputs, setInputs] = useState<CalculatorInputs>({
    currentRevenue: 1000000,
    targetMarketShare: 5,
    investmentAmount: 250000,
    timeframe: 24
  });
  const [results, setResults] = useState<ROIResults | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const currencySymbols: Record<string, string> = {
    USD: '$',
    AUD: 'A$',
    EUR: '€',
    INR: '₹'
  };

  const conversionRates: Record<string, number> = {
    USD: 1,
    AUD: 1.52,
    EUR: 0.92,
    INR: 83.12
  };

  const formatCurrency = (amount: number) => {
    if (!isHydrated) return '0';
    const converted = amount * conversionRates[currency];
    return `${currencySymbols[currency]}${converted.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const calculateROI = () => {
    const indiaMarketSize = 50000000000;
    const projectedRevenue = (indiaMarketSize * inputs.targetMarketShare) / 100;
    const totalReturn = projectedRevenue * (inputs.timeframe / 12);
    const roi = ((totalReturn - inputs.investmentAmount) / inputs.investmentAmount) * 100;
    const monthlyRevenue = projectedRevenue / 12;
    const breakEvenMonths = Math.ceil(inputs.investmentAmount / monthlyRevenue);
    const yearlyGrowth = ((projectedRevenue - inputs.currentRevenue) / inputs.currentRevenue) * 100;

    setResults({
      projectedRevenue,
      roi,
      breakEvenMonths,
      yearlyGrowth
    });
  };

  const handleInputChange = (field: keyof CalculatorInputs, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/partnership-models/components/ROICalculator.tsx:78:6" data-component-path="src/app/partnership-models/components/ROICalculator.tsx" data-component-line="78" data-component-end-line="87" data-component-file="ROICalculator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20shadow-card%20p-8%22%7D" className="bg-card rounded-xl shadow-card p-8">
        <div data-component-id="src/app/partnership-models/components/ROICalculator.tsx:79:8" data-component-path="src/app/partnership-models/components/ROICalculator.tsx" data-component-line="79" data-component-end-line="86" data-component-file="ROICalculator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%22%7D" className="animate-pulse space-y-6">
          <div data-component-id="src/app/partnership-models/components/ROICalculator.tsx:80:10" data-component-path="src/app/partnership-models/components/ROICalculator.tsx" data-component-line="80" data-component-end-line="80" data-component-file="ROICalculator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-surface%20rounded%20w-1%2F3%22%7D" className="h-8 bg-surface rounded w-1/3"></div>
          <div data-component-id="src/app/partnership-models/components/ROICalculator.tsx:81:10" data-component-path="src/app/partnership-models/components/ROICalculator.tsx" data-component-line="81" data-component-end-line="85" data-component-file="ROICalculator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div data-component-id="src/app/partnership-models/components/ROICalculator.tsx:83:14" data-component-path="src/app/partnership-models/components/ROICalculator.tsx" data-component-line="83" data-component-end-line="83" data-component-file="ROICalculator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-20%20bg-surface%20rounded%22%7D" key={i} className="h-20 bg-surface rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}